import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Play, Pause, Eye, Settings, MessageSquare, HelpCircle,
  GitBranch, Zap, Clock, UserPlus, Link, Calendar, Plus, Trash2, GripVertical,
  ChevronRight, X, Brain, Webhook, MousePointer, Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Node type definitions
const NODE_TYPES = {
  message: { 
    label: 'Message', 
    icon: MessageSquare, 
    color: 'bg-blue-500',
    description: 'Send a text message'
  },
  question: { 
    label: 'Question', 
    icon: HelpCircle, 
    color: 'bg-purple-500',
    description: 'Ask a question with options'
  },
  condition: { 
    label: 'Condition', 
    icon: GitBranch, 
    color: 'bg-amber-500',
    description: 'Branch based on conditions'
  },
  ai_intent: { 
    label: 'AI Intent', 
    icon: Brain, 
    color: 'bg-pink-500',
    description: 'Detect intent using AI'
  },
  api_webhook: { 
    label: 'API/Webhook', 
    icon: Webhook, 
    color: 'bg-indigo-500',
    description: 'Call external API or trigger action'
  },
  delay: { 
    label: 'Delay', 
    icon: Clock, 
    color: 'bg-slate-500',
    description: 'Wait before next step'
  },
  human_handoff: { 
    label: 'Human Handoff', 
    icon: UserPlus, 
    color: 'bg-red-500',
    description: 'Transfer to human agent'
  },
  cta_button: { 
    label: 'CTA Button', 
    icon: MousePointer, 
    color: 'bg-emerald-500',
    description: 'Show action buttons'
  },
  link: { 
    label: 'Link', 
    icon: Link, 
    color: 'bg-cyan-500',
    description: 'Share a link or attachment'
  },
  meeting_scheduler: { 
    label: 'Meeting Scheduler', 
    icon: Calendar, 
    color: 'bg-orange-500',
    description: 'Schedule meeting or visit'
  }
};

// Node component
const WorkflowNode = ({ node, isSelected, onSelect, onDelete, onUpdate, onDragStart }) => {
  const nodeType = NODE_TYPES[node.type] || NODE_TYPES.message;
  const IconComponent = nodeType.icon;
  
  return (
    <div
      className={`absolute bg-white dark:bg-slate-800 rounded-lg shadow-lg border-2 transition-all cursor-pointer
        ${isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'}
      `}
      style={{
        left: node.position.x,
        top: node.position.y,
        minWidth: '200px',
        maxWidth: '280px'
      }}
      onClick={() => onSelect(node.id)}
      draggable
      onDragStart={(e) => onDragStart(e, node.id)}
    >
      {/* Header */}
      <div className={`${nodeType.color} text-white px-3 py-2 rounded-t-md flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 cursor-move opacity-70" />
          <IconComponent className="h-4 w-4" />
          <span className="font-medium text-sm">{nodeType.label}</span>
        </div>
        {isSelected && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(node.id); }}
            className="hover:bg-white/20 p-1 rounded"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
      
      {/* Content */}
      <div className="p-3">
        {node.type === 'message' && (
          <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-3">
            {node.data.message || 'Click to edit message...'}
          </p>
        )}
        
        {node.type === 'question' && (
          <div>
            <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
              {node.data.question || 'Click to set question...'}
            </p>
            {node.data.options?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {node.data.options.slice(0, 3).map((opt, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {opt.label}
                  </Badge>
                ))}
                {node.data.options.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{node.data.options.length - 3}
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}
        
        {node.type === 'condition' && (
          <p className="text-sm text-slate-500">
            If: {node.data.condition || 'Set condition...'}
          </p>
        )}
        
        {node.type === 'ai_intent' && (
          <p className="text-sm text-slate-500">
            {node.data.prompt || 'AI will detect intent...'}
          </p>
        )}
        
        {node.type === 'delay' && (
          <p className="text-sm text-slate-500">
            Wait {node.data.duration || 0} {node.data.unit || 'seconds'}
          </p>
        )}
        
        {node.type === 'api_webhook' && (
          <p className="text-sm text-slate-500">
            Action: {node.data.action || 'Configure action...'}
          </p>
        )}
        
        {node.type === 'human_handoff' && (
          <p className="text-sm text-slate-500">
            Transfer to agent
          </p>
        )}
        
        {node.type === 'cta_button' && (
          <div className="flex flex-wrap gap-1">
            {(node.data.buttons || []).slice(0, 2).map((btn, i) => (
              <Badge key={i} className="text-xs bg-emerald-100 text-emerald-700">
                {btn.label}
              </Badge>
            ))}
          </div>
        )}
        
        {node.type === 'link' && (
          <p className="text-sm text-slate-500 truncate">
            {node.data.url || 'Set link URL...'}
          </p>
        )}
        
        {node.type === 'meeting_scheduler' && (
          <p className="text-sm text-slate-500">
            {node.data.type || 'Date'} picker
          </p>
        )}
      </div>
      
      {/* Connection indicator */}
      {node.connections?.length > 0 && (
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs">
          <ChevronRight className="h-4 w-4" />
        </div>
      )}
    </div>
  );
};

// Node palette (left sidebar)
const NodePalette = ({ onAddNode }) => {
  return (
    <div className="w-64 bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 p-4 overflow-y-auto">
      <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Blocks</h3>
      <div className="space-y-2">
        {Object.entries(NODE_TYPES).map(([type, config]) => {
          const IconComponent = config.icon;
          return (
            <button
              key={type}
              onClick={() => onAddNode(type)}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-emerald-400 hover:shadow-md transition-all text-left"
            >
              <div className={`${config.color} p-2 rounded-md`}>
                <IconComponent className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="font-medium text-sm text-slate-900 dark:text-white">{config.label}</p>
                <p className="text-xs text-slate-500">{config.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Node properties panel (right sidebar)
const NodeProperties = ({ node, onUpdate, onClose, properties }) => {
  const [localData, setLocalData] = useState(node?.data || {});
  
  useEffect(() => {
    setLocalData(node?.data || {});
  }, [node]);
  
  if (!node) {
    return (
      <div className="w-80 bg-slate-50 dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 p-4">
        <p className="text-slate-500 text-center py-8">
          Select a block to edit its properties
        </p>
      </div>
    );
  }
  
  const handleChange = (key, value) => {
    const newData = { ...localData, [key]: value };
    setLocalData(newData);
    onUpdate(node.id, { data: newData });
  };
  
  const nodeType = NODE_TYPES[node.type];
  const IconComponent = nodeType?.icon || MessageSquare;
  
  return (
    <div className="w-80 bg-slate-50 dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 overflow-y-auto">
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`${nodeType?.color} p-1.5 rounded`}>
            <IconComponent className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900 dark:text-white">{nodeType?.label}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Message Node */}
        {node.type === 'message' && (
          <>
            <div>
              <Label>Message</Label>
              <Textarea
                value={localData.message || ''}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="Enter your message..."
                rows={4}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Use {'{{variable}}'} for dynamic content
              </p>
            </div>
          </>
        )}
        
        {/* Question Node */}
        {node.type === 'question' && (
          <>
            <div>
              <Label>Question</Label>
              <Textarea
                value={localData.question || ''}
                onChange={(e) => handleChange('question', e.target.value)}
                placeholder="Enter your question..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Variable Name</Label>
              <Input
                value={localData.variable || ''}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="e.g., customer_name"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Options</Label>
              <div className="space-y-2 mt-1">
                {(localData.options || []).map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={opt.label}
                      onChange={(e) => {
                        const newOptions = [...(localData.options || [])];
                        newOptions[i] = { ...newOptions[i], label: e.target.value, value: e.target.value.toLowerCase() };
                        handleChange('options', newOptions);
                      }}
                      placeholder="Option label"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newOptions = (localData.options || []).filter((_, idx) => idx !== i);
                        handleChange('options', newOptions);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChange('options', [...(localData.options || []), { label: '', value: '' }])}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Option
                </Button>
              </div>
            </div>
          </>
        )}
        
        {/* Condition Node */}
        {node.type === 'condition' && (
          <>
            <div>
              <Label>Condition</Label>
              <Input
                value={localData.condition || ''}
                onChange={(e) => handleChange('condition', e.target.value)}
                placeholder="e.g., user_response == 'yes'"
                className="mt-1"
              />
            </div>
          </>
        )}
        
        {/* AI Intent Node */}
        {node.type === 'ai_intent' && (
          <>
            <div>
              <Label>AI Prompt</Label>
              <Textarea
                value={localData.prompt || ''}
                onChange={(e) => handleChange('prompt', e.target.value)}
                placeholder="What should AI detect?"
                rows={3}
                className="mt-1"
              />
            </div>
          </>
        )}
        
        {/* Delay Node */}
        {node.type === 'delay' && (
          <>
            <div>
              <Label>Duration</Label>
              <Input
                type="number"
                value={localData.duration || 0}
                onChange={(e) => handleChange('duration', parseInt(e.target.value))}
                min={0}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Select
                value={localData.unit || 'seconds'}
                onValueChange={(v) => handleChange('unit', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seconds">Seconds</SelectItem>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        
        {/* API/Webhook Node */}
        {node.type === 'api_webhook' && (
          <>
            <div>
              <Label>Action</Label>
              <Select
                value={localData.action || ''}
                onValueChange={(v) => handleChange('action', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="create_call_request">Create Call Request</SelectItem>
                  <SelectItem value="create_visit_request">Create Visit Request</SelectItem>
                  <SelectItem value="create_lead">Create Lead</SelectItem>
                  <SelectItem value="send_email">Send Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Auto Assign Agent</Label>
              <Switch
                checked={localData.auto_assign || false}
                onCheckedChange={(v) => handleChange('auto_assign', v)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Notify Agent</Label>
              <Switch
                checked={localData.notify_agent || false}
                onCheckedChange={(v) => handleChange('notify_agent', v)}
              />
            </div>
          </>
        )}
        
        {/* Human Handoff Node */}
        {node.type === 'human_handoff' && (
          <div>
            <Label>Handoff Message</Label>
            <Textarea
              value={localData.message || 'Transferring you to a human agent...'}
              onChange={(e) => handleChange('message', e.target.value)}
              rows={2}
              className="mt-1"
            />
          </div>
        )}
        
        {/* Link Node */}
        {node.type === 'link' && (
          <>
            <div>
              <Label>Link URL</Label>
              <Input
                value={localData.url || ''}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Link Text</Label>
              <Input
                value={localData.text || ''}
                onChange={(e) => handleChange('text', e.target.value)}
                placeholder="Click here"
                className="mt-1"
              />
            </div>
          </>
        )}
        
        {/* Meeting Scheduler Node */}
        {node.type === 'meeting_scheduler' && (
          <>
            <div>
              <Label>Picker Type</Label>
              <Select
                value={localData.type || 'date_picker'}
                onValueChange={(v) => handleChange('type', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_picker">Date Picker</SelectItem>
                  <SelectItem value="time_slots">Time Slots</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Variable Name</Label>
              <Input
                value={localData.variable || ''}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="e.g., visit_date"
                className="mt-1"
              />
            </div>
          </>
        )}
        
        {/* CTA Button Node */}
        {node.type === 'cta_button' && (
          <div>
            <Label>Buttons</Label>
            <div className="space-y-2 mt-1">
              {(localData.buttons || []).map((btn, i) => (
                <div key={i} className="p-2 bg-white dark:bg-slate-800 rounded border">
                  <Input
                    value={btn.label}
                    onChange={(e) => {
                      const newButtons = [...(localData.buttons || [])];
                      newButtons[i] = { ...newButtons[i], label: e.target.value };
                      handleChange('buttons', newButtons);
                    }}
                    placeholder="Button label"
                    className="mb-2"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const newButtons = (localData.buttons || []).filter((_, idx) => idx !== i);
                      handleChange('buttons', newButtons);
                    }}
                    className="text-red-500"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleChange('buttons', [...(localData.buttons || []), { label: '', action: 'link' }])}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Button
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Workflow Builder Component
const WhatsAppWorkflowBuilder = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const canvasRef = useRef(null);
  
  const [workflow, setWorkflow] = useState({
    name: 'New Workflow',
    description: '',
    trigger_type: 'intent',
    trigger_config: {},
    nodes: [],
    status: 'draft',
    is_default: false,
    priority: 0,
    coexistence_mode: 'parallel',
    assigned_numbers: [],
    tags: []
  });
  
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [properties, setProperties] = useState([]);
  const isNew = workflowId === 'new';
  
  useEffect(() => {
    if (!isNew && workflowId) {
      fetchWorkflow();
    }
    fetchProperties();
  }, [workflowId, token]);
  
  const fetchWorkflow = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/whatsapp-workflows/workflows/${workflowId}?token=${token}`);
      if (response.data.success) {
        setWorkflow(response.data.workflow);
      }
    } catch (error) {
      toast.error('Failed to load workflow');
      navigate('/communication/workflow-builder');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/api/real-estate/properties?token=${token}&limit=100`);
      setProperties(response.data.properties || response.data || []);
    } catch (error) {
      console.error('Failed to fetch properties');
    }
  };
  
  const saveWorkflow = async () => {
    try {
      setSaving(true);
      
      if (isNew) {
        const response = await axios.post(`${API}/api/whatsapp-workflows/workflows?token=${token}`, workflow);
        if (response.data.success) {
          toast.success('Workflow created');
          navigate(`/communication/workflow-builder/${response.data.workflow.id}`);
        }
      } else {
        await axios.put(`${API}/api/whatsapp-workflows/workflows/${workflowId}?token=${token}`, workflow);
        toast.success('Workflow saved');
      }
    } catch (error) {
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };
  
  const addNode = (type) => {
    const newNode = {
      id: `node_${Date.now()}`,
      type,
      position: { x: 100 + (workflow.nodes.length * 50) % 400, y: 100 + Math.floor(workflow.nodes.length / 3) * 150 },
      data: {},
      connections: []
    };
    
    setWorkflow(prev => ({
      ...prev,
      nodes: [...prev.nodes, newNode]
    }));
    setSelectedNodeId(newNode.id);
  };
  
  const updateNode = (nodeId, updates) => {
    setWorkflow(prev => ({
      ...prev,
      nodes: prev.nodes.map(n => n.id === nodeId ? { ...n, ...updates } : n)
    }));
  };
  
  const deleteNode = (nodeId) => {
    setWorkflow(prev => ({
      ...prev,
      nodes: prev.nodes.filter(n => n.id !== nodeId)
    }));
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
    }
  };
  
  const handleNodeDragStart = (e, nodeId) => {
    e.dataTransfer.setData('nodeId', nodeId);
  };
  
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const nodeId = e.dataTransfer.getData('nodeId');
    if (!nodeId || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    updateNode(nodeId, { position: { x, y } });
  };
  
  const handleCanvasDragOver = (e) => {
    e.preventDefault();
  };
  
  const selectedNode = workflow.nodes.find(n => n.id === selectedNodeId);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  return (
    <div className="h-screen flex flex-col bg-slate-100 dark:bg-slate-950" data-testid="whatsapp-workflow-builder">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/communication/workflow-builder')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="border-l border-slate-200 dark:border-slate-700 pl-4">
            <Input
              value={workflow.name}
              onChange={(e) => setWorkflow(prev => ({ ...prev, name: e.target.value }))}
              className="text-lg font-semibold border-0 p-0 h-auto focus-visible:ring-0 bg-transparent"
              placeholder="Workflow Name"
            />
            <p className="text-sm text-slate-500">
              {workflow.nodes.length} nodes · {workflow.status}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button 
            onClick={saveWorkflow} 
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Node Palette */}
        <NodePalette onAddNode={addNode} />
        
        {/* Canvas */}
        <div 
          ref={canvasRef}
          className="flex-1 relative overflow-auto bg-slate-100 dark:bg-slate-950"
          style={{ 
            backgroundImage: 'radial-gradient(circle, #e2e8f0 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
          onDrop={handleCanvasDrop}
          onDragOver={handleCanvasDragOver}
          onClick={() => setSelectedNodeId(null)}
        >
          {workflow.nodes.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600 dark:text-slate-400">
                  Start building your workflow
                </h3>
                <p className="text-slate-500 mt-1">
                  Click a block from the left sidebar to add it
                </p>
              </div>
            </div>
          ) : (
            workflow.nodes.map(node => (
              <WorkflowNode
                key={node.id}
                node={node}
                isSelected={selectedNodeId === node.id}
                onSelect={setSelectedNodeId}
                onDelete={deleteNode}
                onUpdate={updateNode}
                onDragStart={handleNodeDragStart}
              />
            ))
          )}
        </div>
        
        {/* Right Sidebar - Properties */}
        <NodeProperties
          node={selectedNode}
          onUpdate={updateNode}
          onClose={() => setSelectedNodeId(null)}
          properties={properties}
        />
      </div>
      
      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Workflow Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Description</Label>
              <Textarea
                value={workflow.description}
                onChange={(e) => setWorkflow(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this workflow does..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Trigger Type</Label>
              <Select
                value={workflow.trigger_type}
                onValueChange={(v) => setWorkflow(prev => ({ ...prev, trigger_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="intent">AI Intent Detection</SelectItem>
                  <SelectItem value="keyword">Keyword Match</SelectItem>
                  <SelectItem value="manual">Manual Trigger</SelectItem>
                  <SelectItem value="webhook">Webhook</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Coexistence Mode</Label>
              <Select
                value={workflow.coexistence_mode}
                onValueChange={(v) => setWorkflow(prev => ({ ...prev, coexistence_mode: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parallel">Run alongside existing automation</SelectItem>
                  <SelectItem value="override">Override existing automation</SelectItem>
                  <SelectItem value="selective">Selective (for assigned numbers only)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Controls how this workflow interacts with existing WhatsApp automation
              </p>
            </div>
            
            <div>
              <Label>Priority (higher = runs first)</Label>
              <Input
                type="number"
                value={workflow.priority}
                onChange={(e) => setWorkflow(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                min={0}
                max={100}
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Mark as Default Workflow</Label>
              <Switch
                checked={workflow.is_default}
                onCheckedChange={(v) => setWorkflow(prev => ({ ...prev, is_default: v }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowSettings(false)} className="bg-emerald-600">
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppWorkflowBuilder;
